import React, { useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Box, Chip,
} from '@mui/material';
import { AuditReport } from '../../services/customsAuditService';

interface AuditApplyDialogProps {
  open: boolean;
  report: AuditReport;
  appliedFixes: Map<string, any>;
  onConfirm: () => void;
  onCancel: () => void;
}

const AuditApplyDialog: React.FC<AuditApplyDialogProps> = ({
  open, report, appliedFixes, onConfirm, onCancel,
}) => {
  const tiers = useMemo(() => {
    let high = 0, medium = 0, low = 0;
    for (const ar of report.area_results) {
      for (const f of ar.findings) {
        if (!f.suggested_fix || !f.field) continue;
        if (appliedFixes.has(f.field)) continue;
        const conf = f.fix_confidence || 0;
        if (conf >= 0.8) high++;
        else if (conf >= 0.6) medium++;
        else low++;
      }
    }
    return { high, medium, low, total: high + medium };
  }, [report, appliedFixes]);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Apply Audit Fixes</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">High confidence (&gt;80%):</Typography>
            <Chip label={`${tiers.high} fixes`} color="success" size="small" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Medium confidence (60-80%):</Typography>
            <Chip label={`${tiers.medium} fixes`} color="warning" size="small" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">Low confidence (&lt;60%):</Typography>
            <Chip label={`${tiers.low} skipped`} color="default" size="small" />
          </Box>
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1.5, mt: 0.5 }}>
            <Typography variant="body1" fontWeight={600}>
              Total to apply: {tiers.total} fixes
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={onConfirm} disabled={tiers.total === 0}>
          Apply {tiers.total} Fixes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AuditApplyDialog;
